import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { SubscriptionPaymentDto } from "@/Common/Models/Portal/MedfarPortal";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";

interface ITransactionsPaymentPopupProps {
    subscriptionId: string;
    patientContactId: string;
    lateTransactions: SubscriptionPaymentDto[];
    onCloseCallback: () => void;
    isMarkAsPaid: boolean;
}

class TransactionsPaymentPopupState {
    public errors: string[];
    public isLoading: boolean;
    public lateTransactions: SubscriptionPaymentDto[];
    public isMarkAsPaid: boolean;

    public subtotal: number;
    public taxAmounts: TaxAmount[];
    public balance: number;

    constructor(s: TransactionsPaymentPopupState = null) {
        if (s != null) {
            this.isLoading = s.isLoading;
            this.errors = s.errors;
        }
    }
}

class TaxAmount {
    public taxName: string;
    public taxAmount: number;

    constructor(taxName: string) {
        this.taxName = taxName;
        this.taxAmount = 0;
    }
}

class TransactionsPaymentPopup extends React.Component<ITransactionsPaymentPopupProps, TransactionsPaymentPopupState> {
    private componentId = "portal_trx_markaspaid";

    constructor(props: ITransactionsPaymentPopupProps) {
        super(props);

        this.state = this.buildInitialState(props.isMarkAsPaid, props.lateTransactions);
    }

    public componentDidMount(): void {
        overlayStore.showOverlay("TransactionsPaymentPopup");

        const element = $(`#${this.componentId}`);
        const containment = document.body;
        var dragOptions = {
            containment: containment,
            handle: ".dragHandle",
            stop() {
                element.draggable("destroy");
                element.css("height", "auto");
                window.setTimeout(() => {
                    element.draggable(dragOptions);
                }, 50);
            }
        };
        element.draggable(dragOptions);
        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: ITransactionsPaymentPopupProps): void {
        if (prevProps.lateTransactions !== this.props.lateTransactions
            || prevProps.isMarkAsPaid !== this.props.isMarkAsPaid) {
            const newState = new TransactionsPaymentPopupState(this.state);
            newState.lateTransactions = this.props.lateTransactions;
            newState.isMarkAsPaid = this.props.isMarkAsPaid;

            this.setState(newState);
        }

        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("TransactionsPaymentPopup");
    }

    public render(): JSX.Element {
        let subtotal = 0;
        const taxAmounts: TaxAmount[] = [];
        const transactionItems = !this.state.isLoading && this.state.lateTransactions
            ? this.state.lateTransactions.map((latePayment: SubscriptionPaymentDto, i: number) => {
                const listPrice = latePayment.ListPrice;
                subtotal += listPrice;
                let netPrice = listPrice;

                let applicableTaxes: string;
                if (latePayment.Taxable && latePayment.ApplicableTaxes && latePayment.ApplicableTaxes.length) {
                    applicableTaxes = latePayment.ApplicableTaxes.map(p => p.TaxName).join(", ");

                    latePayment.ApplicableTaxes.forEach(taxRule => {
                        const priceForTax = taxRule.IsCascading
                            ? netPrice
                            : listPrice;

                        const taxAmount = priceForTax * taxRule.Rate;
                        netPrice += taxAmount;

                        if (!taxAmounts[taxRule.Order])
                            taxAmounts[taxRule.Order] = new TaxAmount(taxRule.TaxName);

                        taxAmounts[taxRule.Order].taxAmount += taxAmount;
                    });
                }
                else {
                    applicableTaxes = "N/A";
                }

                return (
                    <tr key={"itemdetail_" + i}>
                        <td>{latePayment.Quantity}</td>
                        <td>{latePayment.Description}</td>
                        <td>{latePayment.ListPrice.toFixed(2)}</td>
                        <td>{applicableTaxes}</td>
                        <th>{latePayment.ListPrice.toFixed(2)}</th>
                    </tr>
                );
            })
            : [];

        const taxItems = this.state.taxAmounts.map((taxAmount, i) => (
            <tr key={"taxamount" + i}>
                <td colSpan={3} style={{ textAlign: "right" }}>{taxAmount.taxName}</td>
                <td colSpan={2}>{taxAmount.taxAmount.toFixed(2)}</td>
            </tr>
        ));

        return (
            <div className="popup portal-popup" id={this.componentId}>
                <div className="portal-messages-header">
                    <div className="itemGrow dragHandle">
                        <h3>{cacheStore.GetString("ReportPaidLabel")}</h3>
                    </div>
                    <div>
                        <a className="close" onClick={this.onCloseDialog.bind(this)} aria-label="Close" style={{ paddingTop: "10px" }}>×</a>
                    </div>
                    <h4 />
                </div>

                <div className="p-body myle-form portal-messages-body">
                    <ErrorMessageComponent errors={this.state.errors} />

                    {this.state.isLoading && (this.state.errors == null || this.state.errors.length === 0) &&
                        <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("WaitForPortalLabel")} />
                    }
                    {transactionItems.length && (
                        <table className="trx-item-details" style={{ width: "100%" }}>
                            <thead>
                                <tr>
                                    <td>{cacheStore.GetString("QuantityLabel")}</td>
                                    <td>{cacheStore.GetString("DescriptionLabel")}</td>
                                    <td>{cacheStore.GetString("UnitPriceLabel")}</td>
                                    <td>{cacheStore.GetString("ApplicableTaxesLabel")}</td>
                                    <td>{cacheStore.GetString("PriceLabel")}</td>
                                </tr>
                            </thead>
                            <tbody>
                                {transactionItems}
                            </tbody>
                            <tfoot style={{ borderTop: "lightgray solid 1px" }}>
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "right" }}>{cacheStore.GetString("SubtotalLabel")}</td>
                                    <td colSpan={2}>{subtotal.toFixed(2)}</td>
                                </tr>
                                {taxItems}
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "right" }}>{cacheStore.GetString(this.state.isMarkAsPaid ? "AmountPaidLabel" : "BalanceLabel")}</td>
                                    <td colSpan={2}>{this.state.balance.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
                <div className="p-footer">
                    <div className="col-lg-12 text-right">
                        <button onClick={this.onCloseDialog.bind(this)} className="btn btn-default push-right-5"><span>{cacheStore.GetString("CancelLabel")}</span></button>
                        <button onClick={this.onConfirmClicked.bind(this)} disabled={this.state.isLoading} className="btn btn-primary"><span>{cacheStore.GetString("ConfirmLabel")}</span></button>
                    </div>
                </div>
            </div>
        );
    }

    private onCloseDialog(): void {
        if (this.props.onCloseCallback != null)
            this.props.onCloseCallback();
    }

    private onConfirmClicked(): void {
        var self = this;
        var s = new TransactionsPaymentPopupState(self.state);
        s.isLoading = true;
        self.makePayment();
        self.setState(s);
    }

    private makePayment(): void {
        const self = this;
        const requestData = {
            PatientContactId: self.props.patientContactId,
            SubscriptionId: self.props.subscriptionId,
            TransactionIds: self.props.lateTransactions.map(s => s.PaymentId),
            Amount: self.state.isMarkAsPaid ? 0 : self.state.balance
        };

        let client = new Client();
        client.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_Subscription_MakePayment, requestData, response => self.onComplete(response), err => self.onError(err));
    }

    private onComplete(response: ServerEvent): void {
        let self = this;
        let errors = response.Errors as ErrorMessage[];
        let errorMessages = errors.map((e: ErrorMessage) => e.Message);

        if (errors.length === 0) {
            const op = new LocalEvent(LocalOperations.Portal.PortalManagment_LoadPortalProfileOverview);
            op.AddItem("patientId", self.props.patientContactId);
            LocalEventDispatcher.dispatch(op);
        }
        else {
            var s = new TransactionsPaymentPopupState(self.state);
            s.isLoading = false;
            s.errors = errorMessages;
            self.setState(s);
        }

        self.onCloseDialog();
    }

    private onError(error: string): void {
        var s = new TransactionsPaymentPopupState(this.state);
        s.isLoading = false;
        s.errors = [error];
        this.setState(s);
    }

    private buildInitialState(isMarkAsPaid: boolean, lateTransactions: SubscriptionPaymentDto[]): TransactionsPaymentPopupState {
        let subtotal = 0;
        const taxAmounts: TaxAmount[] = [];
        const initialState = new TransactionsPaymentPopupState();

        initialState.isMarkAsPaid = isMarkAsPaid;
        initialState.lateTransactions = lateTransactions;

        lateTransactions.forEach((latePayment: SubscriptionPaymentDto) => {
            const listPrice = latePayment.ListPrice;
            subtotal += listPrice;

            if (!latePayment.ApplicableTaxes)
                return;

            let netPrice = listPrice;
            latePayment.ApplicableTaxes.forEach(taxRule => {
                const priceForTax = taxRule.IsCascading
                    ? netPrice
                    : listPrice;

                const taxAmount = priceForTax * taxRule.Rate;
                netPrice += taxAmount;

                if (!taxAmounts[taxRule.Order]) {
                    taxAmounts[taxRule.Order] = new TaxAmount(taxRule.TaxName);
                }

                taxAmounts[taxRule.Order].taxAmount += taxAmount;
            });
        });

        subtotal = Math.round(subtotal * 100) / 100;
        initialState.subtotal = subtotal;

        let balance = subtotal;
        const roundedTaxAmounts = taxAmounts.map(taxAmount => {
            const roundedAmount = Math.round(taxAmount.taxAmount * 100) / 100;
            balance += roundedAmount;

            const amt = new TaxAmount(taxAmount.taxName);
            amt.taxAmount = roundedAmount;
            return amt;
        });
        initialState.balance = balance;
        initialState.taxAmounts = roundedTaxAmounts;

        return initialState;
    }
}

export { TransactionsPaymentPopup };
